import { ChevronsRight, BookOpen, FileSearch, Eye, EyeOff, RotateCcw, Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EvidenceLocker } from './EvidenceLocker'
import { DocumentDetailsPanel } from '@/components/library/DocumentDetailsPanel'
import { TriageView } from './TriageView'
import { SurgeryPanel } from './SurgeryPanel'
import { AbbreviationsPanel } from './AbbreviationsPanel'
import type { LibraryItem } from '@/data/mock'
import { useReviewPanel } from '@/contexts/ReviewPanelContext'

export type RightPanelMode = 'review' | 'details'

interface RightPanelProps {
  mode: RightPanelMode
  selectedLibraryItem?: LibraryItem | null
  onClose: () => void
  highlightsVisible?: boolean
  onToggleHighlights?: () => void
}

const modeConfig = {
  review: { title: 'Review', Icon: BookOpen },
  details: { title: 'Document Details', Icon: FileSearch },
}

export function RightPanel({
  mode,
  selectedLibraryItem,
  onClose,
  highlightsVisible = true,
  onToggleHighlights,
}: RightPanelProps) {
  const {
    hasAuditResults,
    apiAuditResult,
    isAuditing,
    isStale,
    selectedClaimId,
    activeSourceId,
    onHighlightClause,
    onSourceReject,
    onReprocessMetadata,
    onUpdateCitation,
    onImportToLibrary,
    onRunAudit,
    onRunIncrementalAudit,
    viewMode,
    surgeryClaimId,
  } = useReviewPanel()

  const { title, Icon } = modeConfig[mode]

  return (
    <aside className="w-[320px] max-w-[320px] shrink-0 border-l border-border bg-white flex flex-col h-full overflow-hidden">
      {/* Header - matches Workbench header height (h-12) */}
      <div className="h-12 flex items-center justify-between px-4 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        </div>
        <div className="flex items-center gap-1">
          {mode === 'review' && hasAuditResults && (
            isAuditing ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground px-3 py-1.5">
                <Loader2 className="h-[13px] w-[13px] shrink-0 animate-spin" strokeWidth={1.5} />
                Auditing...
              </span>
            ) : isStale ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 bg-[#E1F5EE] text-[#0F6E56] text-xs font-medium px-3 py-1.5 rounded-md hover:bg-[#C8EDE0] transition-colors"
                onClick={onRunIncrementalAudit ?? onRunAudit}
              >
                <RotateCcw className="h-[13px] w-[13px] shrink-0" strokeWidth={1.5} />
                Audit new text
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground text-xs px-2">
                <Check className="h-3 w-3 shrink-0" strokeWidth={1.5} />
                All text audited
              </span>
            )
          )}
          {mode === 'review' && hasAuditResults && onToggleHighlights && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              onClick={onToggleHighlights}
              title={highlightsVisible ? 'Hide highlights' : 'Show highlights'}
            >
              {highlightsVisible ? <Eye className="h-4 w-4" strokeWidth={1.5} /> : <EyeOff className="h-4 w-4" strokeWidth={1.5} />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground"
            onClick={onClose}
          >
            <ChevronsRight className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">

        {/* ── Triage View (primary review mode) ── */}
        {mode === 'review' && viewMode === 'triage' && (
          <TriageView />
        )}

        {/* ── Surgery View ── */}
        {mode === 'review' && viewMode === 'surgery' && surgeryClaimId !== null && (
          <SurgeryPanel />
        )}
        {mode === 'review' && viewMode === 'surgery' && surgeryClaimId === null && (
          <TriageView />
        )}

        {/* ── Abbreviations View ── */}
        {mode === 'review' && viewMode === 'abbreviations' && (
          <AbbreviationsPanel />
        )}

        {/* ── Mock EvidenceLocker (legacy demo flow) ── */}
        {mode === 'review' && hasAuditResults && !apiAuditResult && viewMode === 'triage' && (
          <EvidenceLocker
            selectedClaimId={selectedClaimId}
            activeSourceId={activeSourceId}
            onHighlightClause={onHighlightClause}
            onImportToLibrary={onImportToLibrary}
            onSourceReject={onSourceReject}
          />
        )}

        {mode === 'details' && selectedLibraryItem && (
          <DocumentDetailsPanel item={selectedLibraryItem} onReprocess={onReprocessMetadata} onUpdateCitation={onUpdateCitation} />
        )}
      </div>

      {/* PDF Viewer Modal will be wired in Phase 3 (Surgery view) */}
    </aside>
  )
}
